import { gql, type ApolloClient, type TypedDocumentNode } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { GQL } from "@binale-tech/shared";
import { useEffect, useState } from "react";

export const AUTOMATION_JOB_POLL_MS = 2000;
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

const jobFields = gql`
    fragment AutomationJobFields on AutomationJob {
        id
        kind
        status
        total
        succeeded
        skipped
        failed
        error
        createdAt
        startedAt
        finishedAt
        documentsToRecordsResults {
            documentId
            outcome
            mode
            recordIds
            reason
            message
        }
    }
`;

export const queryAutomationJob: TypedDocumentNode<
    Pick<GQL.IQuery, "automationJob">,
    GQL.IQueryAutomationJobArgs
> = gql`
    query automationJob($companyId: ID!, $id: ID!) {
        automationJob(companyId: $companyId, id: $id) {
            ...AutomationJobFields
        }
    }
    ${jobFields}
`;

export const mutationDmsDocumentsToRecordsEnqueue: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsDocumentsToRecordsEnqueue">,
    GQL.IMutationDmsDocumentsToRecordsEnqueueArgs
> = gql`
    mutation dmsDocumentsToRecordsEnqueue($input: DmsDocumentsToRecordsInput!) {
        dmsDocumentsToRecordsEnqueue(input: $input) {
            ...AutomationJobFields
        }
    }
    ${jobFields}
`;

export function isJobFinished(job: Pick<GQL.IAutomationJob, "status"> | null | undefined): boolean {
    return job?.status === GQL.IAutomationJobStatus.Done || job?.status === GQL.IAutomationJobStatus.Failed;
}

/** Polls a job while it is QUEUED/RUNNING. Stops on DONE/FAILED, on unmount and while jobId is null. */
export function useAutomationJob(
    companyId: string | undefined,
    jobId: string | null,
): { job: GQL.IAutomationJob | null; isFinished: boolean; error: Error | null } {
    const [finishedJobId, setFinishedJobId] = useState<string | null>(null);
    const { data, error } = useQuery(queryAutomationJob, {
        variables: { companyId: companyId ?? "", id: jobId ?? "" },
        skip: !companyId || !jobId,
        fetchPolicy: "no-cache",
        pollInterval: finishedJobId === jobId ? 0 : AUTOMATION_JOB_POLL_MS,
    });

    const job = jobId ? (data?.automationJob ?? null) : null;
    const isFinished = isJobFinished(job);

    useEffect(() => {
        if (isFinished && jobId) {
            setFinishedJobId(jobId);
        }
    }, [isFinished, jobId]);

    return { job, isFinished, error: error ? new Error(error.message) : null };
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Imperative variant of useAutomationJob for callers that must outlive their component
 * (a toast that resolves when the job ends). Rejects after repeated query failures.
 */
export async function waitForAutomationJob(
    client: Pick<ApolloClient, "query">,
    args: { companyId: string; jobId: string; onProgress?: (job: GQL.IAutomationJob) => void },
): Promise<GQL.IAutomationJob> {
    let consecutiveErrors = 0;
    for (;;) {
        try {
            const { data } = await client.query({
                query: queryAutomationJob,
                variables: { companyId: args.companyId, id: args.jobId },
                fetchPolicy: "no-cache",
            });
            consecutiveErrors = 0;
            const job = data?.automationJob;
            if (!job) {
                throw new Error("Job not found");
            }
            args.onProgress?.(job);
            if (isJobFinished(job)) {
                return job;
            }
        } catch (e) {
            consecutiveErrors += 1;
            if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
                throw e;
            }
        }
        await sleep(AUTOMATION_JOB_POLL_MS);
    }
}

export interface DocumentsToRecordsSummary {
    created: number;
    withoutAccount: number;
    alreadyBooked: number;
    refused: number;
    failed: number;
}

export function summarizeDocumentsToRecords(
    results: readonly GQL.IDmsDocumentToRecordResult[],
): DocumentsToRecordsSummary {
    const summary: DocumentsToRecordsSummary = {
        created: 0,
        withoutAccount: 0,
        alreadyBooked: 0,
        refused: 0,
        failed: 0,
    };
    for (const r of results) {
        switch (r.outcome) {
            case GQL.IDmsDocumentToRecordOutcome.Created:
                if (r.mode === GQL.IDmsDocumentToRecordMode.FallbackNoAccount) {
                    summary.withoutAccount += 1;
                } else {
                    summary.created += 1;
                }
                break;
            case GQL.IDmsDocumentToRecordOutcome.AlreadyBooked:
                summary.alreadyBooked += 1;
                break;
            case GQL.IDmsDocumentToRecordOutcome.Refused:
                summary.refused += 1;
                break;
            case GQL.IDmsDocumentToRecordOutcome.Failed:
                summary.failed += 1;
                break;
        }
    }
    return summary;
}
